/**
 * In-place edit overlay (TEC-465 P3) — the inverse of the catalogue. A reviewer
 * toggles inspector mode on the live site; declaratively-rendered messages
 * (wrapped by <Msg>) become clickable and open an editor for the active locale.
 * Saves go through the SAME write path and SAME ICU validation as the catalogue
 * (one store, one status contract, one check).
 *
 * Renders nothing when inspector mode is not available (non-reviewers can never
 * reach the editor, even if the flag is forced). Mount once under IntlProvider.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18nInspector } from './inspector';
import { fetchEntry, saveOverride, SaveOverrideError } from './api';
import { StatusBadge } from './StatusBadge';
import { REVIEW_LOCALES, isReviewLocale, type CatalogueEntry, type ReviewLocale } from '../core/types';

const MARKER_SELECTOR = '[data-i18n-id]';

const OVERLAY_STYLE = `
.i18n-inspect-on [data-i18n-inspectable] {
  outline: 1px dashed rgba(59,130,246,0.7);
  outline-offset: 1px;
  cursor: pointer;
}
.i18n-inspect-on [data-i18n-inspectable]:hover {
  outline: 2px solid rgba(59,130,246,1);
  background: rgba(59,130,246,0.08);
}
`;

function findMarker(el: HTMLElement | null): HTMLElement | null {
  return el ? (el.closest(MARKER_SELECTOR) as HTMLElement | null) : null;
}

/** Locale of the rendered page, from the URL prefix (so the overlay needn't sit under IntlProvider). */
function localeFromPathname(): string {
  if (typeof window === 'undefined') return 'en';
  const first = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && ['en', 'fi', 'sv'].includes(first) ? first : 'en';
}

interface EditorTarget {
  id: string;
  /** The DOM marker we update in place after a successful save (if present). */
  element: HTMLElement | null;
}

export function InspectorOverlay(): React.ReactElement | null {
  const { available, enabled, setEnabled, focusId, setFocusId } = useI18nInspector();
  const pageLocale = localeFromPathname();
  const defaultLocale: ReviewLocale = isReviewLocale(pageLocale) ? pageLocale : 'fi';

  const [target, setTarget] = useState<EditorTarget | null>(null);

  // Toggle a body class so the marker outline CSS applies only while enabled.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('i18n-inspect-on', enabled);
    return () => document.body.classList.remove('i18n-inspect-on');
  }, [enabled]);

  // Capture clicks on marked messages while enabled.
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const handler = (e: MouseEvent): void => {
      const marker = findMarker(e.target as HTMLElement);
      if (!marker) return;
      const id = marker.getAttribute('data-i18n-id');
      if (!id) return;
      e.preventDefault();
      e.stopPropagation();
      setTarget({ id, element: marker });
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [enabled]);

  // Deep-link focus: open the editor for ?i18nInspect=<id> and scroll to it.
  useEffect(() => {
    if (!enabled || !focusId) return;
    const marker = document.querySelector(`[data-i18n-id="${CSS.escape(focusId)}"]`) as
      | HTMLElement
      | null;
    marker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTarget({ id: focusId, element: marker });
    setFocusId(null);
  }, [enabled, focusId, setFocusId]);

  if (!available) return null;

  return (
    <>
      <style>{OVERLAY_STYLE}</style>

      {/* Floating reviewer control. */}
      <div
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2147483000 }}
        className="rounded-lg bg-jc-elevated shadow-lg ring-1 ring-jc-border p-2 text-xs"
      >
        <button
          type="button"
          onClick={() => setEnabled(!enabled)}
          className={`backoffice-touch rounded px-2 py-1 font-semibold ${
            enabled ? 'bg-blue-600 text-white' : 'bg-jc-page text-jc-ink ring-1 ring-jc-border'
          }`}
        >
          {enabled ? 'Inspector: ON' : 'Inspect translations'}
        </button>
        {enabled && (
          <div className="mt-1 text-jc-ink-secondary">Click any dashed string to edit.</div>
        )}
      </div>

      {target && (
        <OverlayEditor
          id={target.id}
          element={target.element}
          defaultLocale={defaultLocale}
          renderedLocale={pageLocale}
          onClose={() => setTarget(null)}
        />
      )}
    </>
  );
}

interface OverlayEditorProps {
  id: string;
  element: HTMLElement | null;
  defaultLocale: ReviewLocale;
  /** The locale the marked element is actually rendering (the page locale). */
  renderedLocale: string;
  onClose: () => void;
}

function OverlayEditor({
  id,
  element,
  defaultLocale,
  renderedLocale,
  onClose,
}: OverlayEditorProps): React.ReactElement {
  const [entry, setEntry] = useState<CatalogueEntry | null>(null);
  const [locale, setLocale] = useState<ReviewLocale>(defaultLocale);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const loadErrorRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchEntry(id)
      .then((row) => {
        if (cancelled) return;
        setEntry(row);
        setDraft(row.locales[locale].value);
      })
      .catch((e) => {
        if (!cancelled) loadErrorRef.current = e instanceof Error ? e.message : 'Failed to load';
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // When the reviewer switches locale, reset the draft to that locale's value.
  useEffect(() => {
    if (entry) setDraft(entry.locales[locale].value);
  }, [locale, entry]);

  const save = useCallback(async () => {
    if (!entry) return;
    setSaving(true);
    setErrors([]);
    try {
      await saveOverride({ id, locale, value: draft });
      // Live in-place update without a full reload. The marker shows the raw
      // saved string (ICU placeholders render literally until the next build).
      // Only update the DOM when editing the locale that is actually rendered.
      if (element && locale === renderedLocale) {
        element.textContent = draft;
      }
      onClose();
    } catch (e) {
      if (e instanceof SaveOverrideError) {
        setErrors(e.icuErrors.length ? e.icuErrors : [e.message]);
      } else {
        setErrors([e instanceof Error ? e.message : 'Save failed']);
      }
    } finally {
      setSaving(false);
    }
  }, [entry, id, locale, draft, element, onClose]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2147483001 }}
      className="flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-jc-elevated p-4 shadow-2xl ring-1 ring-jc-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <code className="break-words text-xs text-jc-ink-secondary">{id}</code>
          <button type="button" onClick={onClose} className="text-jc-ink-secondary">
            ✕
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-jc-ink-secondary">Loading…</p>
        ) : !entry ? (
          <p className="text-sm text-red-600">{loadErrorRef.current ?? 'Not found'}</p>
        ) : (
          <>
            {entry.description && (
              <p className="mb-2 text-xs text-jc-ink-secondary">{entry.description}</p>
            )}
            <div className="mb-2 rounded bg-jc-page p-2 text-sm text-jc-ink">
              <span className="text-[10px] uppercase tracking-wide text-jc-ink-secondary">
                EN reference
              </span>
              <div className="whitespace-pre-wrap break-words">{entry.source}</div>
            </div>

            <div className="mb-2 flex items-center gap-2">
              <label className="text-xs text-jc-ink-secondary">Locale</label>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value as ReviewLocale)}
                className="backoffice-control backoffice-touch text-sm"
              >
                {REVIEW_LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
              <StatusBadge status={entry.locales[locale].status} />
            </div>

            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              className="backoffice-control w-full text-sm"
              disabled={saving}
              autoFocus
            />

            {errors.length > 0 && (
              <ul className="mt-2 rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="backoffice-btn-primary backoffice-touch text-sm"
              >
                {saving ? 'Saving…' : 'Save correction'}
              </button>
              <button type="button" onClick={onClose} disabled={saving} className="text-sm text-jc-ink-secondary">
                Cancel
              </button>
              <span className="ml-auto text-[11px] text-jc-ink-secondary">
                Imperative strings (titles, aria-labels) aren’t editable here — use the catalogue.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
